/**
 * MeetingSummarizerUI — Meeting Summarizer (/tools/meeting-summary)
 *
 * Mockup-faithful reorganization (Meetingcite / Meeting Summarizer Mockup):
 *  - Landing: history log — criteria chip strip from the last run (click to
 *    pre-fill) + compact digest cards.
 *  - New Digest: two columns — left = places/date/filters/meetings + Generate;
 *    right (sticky) = month calendar with meeting-day dots + day picker, and
 *    the Alerts panel.
 *  - Result: grouped digest with a Print + Save PDF toolbar.
 *
 * All capabilities from the prior design are preserved: entitled-places
 * picker with breadcrumbs, search-to-add, live-wired filters (meeting types,
 * governing bodies incl. the Unclassified bucket, analysis types), the
 * 25-meeting cap footer, per-meeting selection, alert prefs persistence,
 * history re-render (JSON digests + plain-text block fallback), coverage
 * banner, confidence flags, sources, print stylesheet.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiFetch } from '@/lib/apiFetch';
import { MessageActions } from './MessageActions';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MapPin,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Printer,
  FileDown,
  FileText,
  PlusCircle,
  AlertTriangle,
  ListChecks,
  X,
  Search,
  Check,
  Send,
  Paperclip,
  Trash2,
} from 'lucide-react';
import type { Tool, JurisdictionTreeNode } from '@/types/database';

const UNCLASSIFIED_BODY = '__unclassified__';

/**
 * Classifications that ANCHOR a meeting (parity with the engine). Everything
 * else (Report, Resolution, Memo, Notice, Bid, Contract…) is supporting
 * material — it attaches to an anchored meeting but never creates one.
 */
const MEETING_RECORD_TYPES = [
  'Minutes',
  'Agenda',
  'Transcript',
  'Packet',
  'Special Meeting',
  'Public Hearing',
  'Workshop',
  'Meeting',
  'Meeting Information',
];

// ── Types ────────────────────────────────────────────────────────────────────

interface ArtifactRow {
  id: string;
  url: string;
  kind?: string | null;
  meeting_type?: string | null;
  meeting_date?: string | null;
  department?: string | null;
  governing_body?: string | null;
  jurisdiction_id?: string | null;
}

interface MeetingView {
  key: string;
  jurisdictionId: string;
  jurisdictionName: string;
  governingBody: string | null;
  meetingDate: string;
  types: string[];
}

interface ToolRun {
  id: string;
  status: string;
  created_at: string;
  latency_ms?: number | null;
  input?: {
    run_scope?: {
      place_ids?: string[];
      jurisdiction_names?: Record<string, string> | null;
      start_date?: string | null;
      end_date?: string | null;
    };
  } | null;
  output?: { blocks?: Array<{ type: string; content: string }> } | null;
}

interface DigestPlace {
  place: string;
  headline?: string | null;
  meetings: Array<{
    meeting_date?: string;
    governing_body?: string | null;
    meeting_types?: string[];
    summary?: string | null;
    agenda_items?: Array<{
      title?: string;
      discussion?: string;
      outcome?: string | null;
      people?: string[];
    }> | null;
    motions?: Array<{
      motion_text?: string;
      moved_by?: string | null;
      seconded_by?: string | null;
      result?: string;
      vote_type?: string;
      vote_breakdown?: Array<{ member?: string; vote?: string }> | null;
    }> | null;
    confidence?: string;
    confidence_reason?: string | null;
  }>;
}

interface Digest {
  window?: { start?: string; end?: string };
  coverage?: {
    included_meetings?: number;
    excluded_meetings_count?: number;
    capped?: boolean;
  };
  places?: DigestPlace[];
  places_with_no_meetings_in_window?: string[];
  framework?: {
    whats_new?: string[];
    what_changed?: Array<{
      issue?: string;
      earlier?: string;
      later?: string;
      change?: string;
    }>;
    contradictions?: Array<{
      item?: string;
      documents?: string;
      discrepancy?: string;
    }>;
    verification_needed?: string[];
    reporting_leads?: string[];
    records_to_request?: Array<{ question?: string; record?: string }>;
    story_angles?: Array<{
      angle?: string;
      evidence?: string;
      missing?: string;
    }>;
    sources_map?: Array<{
      place?: string;
      document?: string;
      page?: string | null;
    }>;
  };
}

interface Citation {
  artifactId?: string;
  artifactTitle?: string;
  artifactUrl?: string;
}

interface PlaceOption {
  id: string;
  name: string;
  level?: string;
  /** Ancestor chain: ["New Jersey", "Hudson", "Jersey City"] */
  path: string[];
}

interface Props {
  tool: Tool;
  workspaceId?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const LEVEL_LABEL: Record<string, string> = {
  state: 'State',
  county: 'County',
  place: 'City/Town',
  body: 'Body',
};

function ltreeSegments(path: string | null | undefined): string[] {
  if (!path) return [];
  return path
    .split('.')
    .map((seg) => seg.trim().replace(/^"|"$/g, ''))
    .filter(Boolean);
}

function typeLabel(level?: string): string {
  return LEVEL_LABEL[level ?? ''] ?? level ?? '';
}

function flattenPlaces(
  nodes: JurisdictionTreeNode[],
  ancestors: string[] = []
): PlaceOption[] {
  const out: PlaceOption[] = [];
  for (const n of nodes ?? []) {
    if (!n.id || !n.name) continue;
    if (n.level !== 'body') {
      out.push({
        id: n.id,
        name: n.name,
        level: n.level,
        path: [...ancestors, n.name],
      });
    }
    if (n.children?.length)
      out.push(...flattenPlaces(n.children, [...ancestors, n.name]));
  }
  return out;
}

function meetingBody(a: ArtifactRow): string | null {
  return a.department ?? a.governing_body ?? null;
}

function meetingKey(
  jurisdictionId: string,
  body: string | null,
  date: string
): string {
  return `${jurisdictionId}::${body ?? ''}::${date}`;
}

function isoToday(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Render a date-only string ("YYYY-MM-DD") in the LOCAL calendar date.
 *
 * new Date("2026-06-02") parses as UTC MIDNIGHT, so rendering it with
 * toLocaleDateString shifts a day backward in any timezone west of UTC
 * (Jun 2 → "Jun 1"). Constructing a LOCAL noontime Date keeps the display
 * identical to the stored calendar date in every timezone and avoids
 * DST/day-boundary shifts. Non date-only inputs fall back to raw parsing.
 */
function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const ymd = String(iso).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0).toLocaleDateString(undefined, {
      dateStyle: 'medium',
    });
  }
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      dateStyle: 'medium',
    });
  } catch {
    return iso;
  }
}

function parseDigestBlock(run: ToolRun): Digest | null {
  const blocks = run.output?.blocks ?? [];
  const jsonBlock = blocks.find((b) => b.type === 'json') ?? blocks[0];
  if (!jsonBlock?.content) return null;
  try {
    return JSON.parse(jsonBlock.content) as Digest;
  } catch {
    return null;
  }
}

/**
 * Descriptive title for a stored summary, e.g.
 * "Jersey City, New Jersey City Council (Aug 19, 2026)" — place, state,
 * governing body and meeting date from the stored digest + jurisdiction meta.
 */
function summaryTitle(
  run: ToolRun,
  placeMeta?: Map<string, PlaceOption>
): string {
  const parsed = parseDigestBlock(run);
  const p = parsed?.places?.[0];
  const m = p?.meetings?.[0];
  const mCount = p?.meetings?.length ?? 0;
  const metaP = p?.place ? placeMeta?.get(p.place) : undefined;
  const state =
    metaP?.path?.[0] && metaP.path[0] !== p?.place ? metaP.path[0] : '';
  const base = [
    p?.place ?? '',
    state ? `, ${state}` : '',
    m?.governing_body ? ` ${m.governing_body}` : '',
    m?.meeting_date ? ` (${fmtDate(m.meeting_date)})` : '',
  ].join('');
  if (base.trim()) return mCount > 1 ? `${base} +${mCount - 1} more` : base;
  const rs = run.input?.run_scope;
  const label = rs?.jurisdiction_names
    ? (Object.values(rs.jurisdiction_names)[0] ?? 'Summary')
    : 'Summary';
  return `${label} · ${fmtDate(run.created_at)}`;
}

/** Raw text fallback for runs whose stored output is plain text. */
function runTextBlock(run: ToolRun): string {
  const blocks = run.output?.blocks ?? [];
  const text = blocks.find((b) => b.type === 'text') ?? blocks[0];
  return text && typeof text.content === 'string' ? text.content : '';
}

/** First day + last day of a YYYY-MM month. */
function currentMonth(): string {
  // LOCAL calendar month — the visible month must match the user's clock.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthBounds(ym: string): { start: string; end: string } {
  const [y, m] = ym.split('-').map(Number);
  const start = `${y}-${String(m).padStart(2, '0')}-01`;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    start,
    end: `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`,
  };
}

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The month a date-range should show in the calendar: the range's start
 * month if the span is contained in it; otherwise the month that contains
 * the MOST days of the range, breaking ties toward the most recent month.
 */
// Markdown prose styles for rendered digest / chat content — bold headers,
// bold + italic emphasis, black text, comfortable base size (shared with the
// normal chat look).
const MD_CLASSES = `prose prose-base max-w-none text-black
  prose-p:my-2 prose-p:leading-7 prose-p:text-black
  prose-headings:font-bold prose-headings:text-black prose-headings:mt-4 prose-headings:mb-2
  prose-h1:text-lg prose-h2:text-base prose-h3:text-base
  prose-ul:my-2 prose-ul:pl-6 prose-ol:my-2 prose-ol:pl-6
  prose-li:my-1.5 prose-li:text-black
  prose-strong:font-bold prose-strong:text-black
  prose-em:text-black
  prose-blockquote:border-l-4 prose-blockquote:border-blue-400 prose-blockquote:bg-blue-50 prose-blockquote:pl-4 prose-blockquote:py-1 prose-blockquote:rounded-r prose-blockquote:text-black prose-blockquote:not-italic
  prose-a:text-blue-700 prose-a:underline hover:prose-a:text-blue-900
  prose-code:bg-gray-100 prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:font-mono
  prose-table:text-sm prose-th:bg-gray-100 prose-th:text-black prose-th:font-bold prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-gray-300 prose-td:px-3 prose-td:py-2 prose-td:text-black`;

// ── Small UI atoms ───────────────────────────────────────────────────────────

function Chip({
  label,
  onRemove,
  onClick,
  tone = 'default',
}: {
  label: string;
  onRemove?: () => void;
  onClick?: () => void;
  tone?: 'default' | 'indigo' | 'amber';
}) {
  const toneCls =
    tone === 'indigo'
      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${toneCls} ${
        onClick ? 'cursor-pointer hover:opacity-80' : ''
      }`}
    >
      {label}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="hover:text-red-500"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export const MeetingSummarizerUI: React.FC<Props> = ({ tool }) => {
  const navigate = useNavigate();

  // ── Places ─────────────────────────────────────────────────────────────
  const [placeFilter, setPlaceFilter] = useState('');
  const [placesOpen, setPlacesOpen] = useState(false);
  const [placesLoading, setPlacesLoading] = useState(true);
  const [placesError, setPlacesError] = useState<string | null>(null);
  const [basePlaces, setBasePlaces] = useState<PlaceOption[]>([]);
  const [searchHits, setSearchHits] = useState<PlaceOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    setPlacesLoading(true);
    setPlacesError(null);
    apiFetch('/api/jurisdictions/tree')
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error ?? 'Failed to load places'
          );
        }
        return r.json();
      })
      .then((data) => {
        if (!cancelled) {
          const nodes = (
            Array.isArray(data) ? data : (data?.data ?? [])
          ) as JurisdictionTreeNode[];
          setBasePlaces(flattenPlaces(nodes));
        }
      })
      .catch((err) => {
        if (!cancelled)
          setPlacesError(
            err instanceof Error ? err.message : 'Failed to load places'
          );
      })
      .finally(() => {
        if (!cancelled) setPlacesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const q = placeFilter.trim();
    if (q.length < 2) {
      setSearchHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      apiFetch(`/api/jurisdictions/search?q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((data) => {
          if (!cancelled) {
            const hits = (
              Array.isArray(data) ? data : (data?.data ?? [])
            ) as Array<{
              id: string;
              name: string;
              level?: string;
              path?: string | null;
            }>;
            setSearchHits(
              hits
                .filter((h) => h.id && h.name)
                .filter((h) => !basePlaces.some((b) => b.id === h.id))
                .map((h) => ({
                  id: h.id,
                  name: h.name,
                  level: h.level,
                  path: ltreeSegments(h.path),
                }))
            );
          }
        })
        .catch(() => {});
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [placeFilter, basePlaces]);

  const places = useMemo(() => {
    const merged = new Map<string, PlaceOption>();
    for (const p of basePlaces) merged.set(p.id, p);
    for (const h of searchHits) merged.set(h.id, h);
    return [...merged.values()].filter((p) => {
      const needle = placeFilter.trim().toLowerCase();
      if (!needle) return true;
      return [p.name, ...p.path].some((seg) =>
        seg.toLowerCase().includes(needle)
      );
    });
  }, [basePlaces, searchHits, placeFilter]);

  const placeName = (id: string): string =>
    basePlaces.find((p) => p.id === id)?.name ??
    searchHits.find((p) => p.id === id)?.name ??
    id;

  // Name-indexed jurisdiction metadata for the Result view (type + ancestry).
  const placeMetaByName = useMemo(() => {
    const m = new Map<string, PlaceOption>();
    for (const p of [...basePlaces, ...searchHits]) {
      if (!m.has(p.name)) m.set(p.name, p);
    }
    return m;
  }, [basePlaces, searchHits]);

  // ── View state ───────────────────────────────────────────────────────────
  const [view, setView] = useState<'history' | 'create' | 'result'>('history');
  const [digest, setDigest] = useState<Digest | null>(null);
  const [citations, setCitations] = useState<Citation[]>([]);
  const [legacyText, setLegacyText] = useState('');
  // The digest run shown in the result view — echoed into the URL for debug.
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);

  // ── History ──────────────────────────────────────────────────────────────
  const [runs, setRuns] = useState<ToolRun[]>([]);
  /** Standardized feedback: reasons + restored votes for summary runs. */
  const [summaryReasons, setSummaryReasons] = useState<{
    up: { code: string; label: string }[];
    down: { code: string; label: string }[];
  }>({ up: [], down: [] });
  const [summaryVotes, setSummaryVotes] = useState<Map<string, 'up' | 'down'>>(
    new Map()
  );

  useEffect(() => {
    apiFetch('/api/feedback/reasons?tool_slug=meeting-summary')
      .then((r) => r.json())
      .then(
        (d: {
          data?: Array<{ code: string; label: string; polarity: string }>;
        }) => {
          setSummaryReasons({
            up: (d?.data ?? []).filter((r) => r.polarity === 'up'),
            down: (d?.data ?? []).filter((r) => r.polarity === 'down'),
          });
        }
      )
      .catch(() => {
        /* non-critical */
      });
  }, []);

  const submitSummaryFeedback = async (
    toolRunId: string,
    rating: 'up' | 'down',
    reasonCode?: string,
    comment?: string
  ) => {
    try {
      const res = await apiFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          tool_run_id: toolRunId,
          rating,
          ...(reasonCode ? { reason_code: reasonCode } : {}),
          ...(comment ? { comment } : {}),
        }),
      });
      const j = (await res.json().catch(() => null)) as {
        action_triggered?: 'reprocess' | 'admin_review' | null;
      } | null;
      setSummaryVotes((prev) => {
        const next = new Map(prev);
        next.set(toolRunId, rating);
        return next;
      });
      return {
        actionTriggered: (j?.action_triggered ?? null) as
          | 'reprocess'
          | 'admin_review'
          | null,
      };
    } catch {
      return { actionTriggered: null };
    }
  };
  const [runsLoading, setRunsLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadRuns = () => {
    setRunsLoading(true);
    setHistoryError(null);
    apiFetch(`/api/tool-runs?tool_id=${tool.id}&limit=25`)
      .then((r) => r.json())
      .then((data) => setRuns(Array.isArray(data) ? data : (data?.data ?? [])))
      .catch(() => setHistoryError('Could not load digest history.'))
      .finally(() => setRunsLoading(false));
  };

  useEffect(() => {
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool.id]);

  const lastRun = runs.find((r) => r.status === 'succeeded') ?? runs[0];

  // Soft-hide confirm modal state (checkbox-gated, keeps the DB row)
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const handleDeleteRun = (runId: string) => {
    setConfirmChecked(false);
    setPendingDelete(runId);
  };

  /** The Meeting Digest page delete — opens the same confirm modal. */
  const handleDeleteCurrent = () => {
    if (currentRunId) handleDeleteRun(currentRunId);
  };

  /** Remove from the user's list of summaries — NOT deleted from the DB. */
  const confirmHideDelete = async () => {
    const runId = pendingDelete;
    if (!runId) return;
    try {
      await apiFetch(`/api/tool-runs/${runId}/hide`, { method: 'PUT' });
    } catch {
      /* proceed with local cleanup regardless */
    }
    setPendingDelete(null);
    setConfirmChecked(false);
    if (runId === currentRunId) {
      setDigest(null);
      setCitations([]);
      setLegacyText('');
      setCurrentRunId(null);
      setView('history');
    }
    loadRuns();
  };

  /** Pre-fill the New Digest form from a saved run's criteria chips. */
  const prefillFromRun = (run: ToolRun) => {
    const rs = run.input?.run_scope;
    if (rs?.place_ids?.length) setSelectedPlaces(rs.place_ids);
    if (rs?.start_date) setDateFrom(rs.start_date);
    if (rs?.end_date) setDateTo(rs.end_date);
    setView('create');
  };

  // ── New Digest form ─────────────────────────────────────────────────────
  const [selectedPlaces, setSelectedPlaces] = useState<string[]>([]);
  // The date window is UNBOUNDED — selection happens exclusively through the
  // calendar, so the user manages dates manually. Run resolution includes
  // every checked meeting (capped at 25 newest).
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [meetingTypes, setMeetingTypes] = useState<string[]>([]);
  const [governingBodies, setGoverningBodies] = useState<string[]>([]);
  const [bodyOptions, setBodyOptions] = useState<string[]>([]);
  const [analysisTypes, setAnalysisTypes] = useState<string[]>(['summary']);
  const [meetings, setMeetings] = useState<MeetingView[]>([]);
  const [checkedMeetings, setCheckedMeetings] = useState<Set<string>>(
    new Set()
  );
  // Key → MeetingView for every CHECKED meeting (calendar day picks drive this).
  const [selectedViews, setSelectedViews] = useState<Map<string, MeetingView>>(
    new Map()
  );
  const [capExcluded, setCapExcluded] = useState(0);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  // Live progress for long generations — never looks frozen.
  const [elapsedSec, setElapsedSec] = useState(0);
  const [queuedCount, setQueuedCount] = useState(0);

  useEffect(() => {
    if (!running) {
      setElapsedSec(0);
      return;
    }
    const id = window.setInterval(() => setElapsedSec((e) => e + 1), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  // A new search (place change) must never keep showing a previous summary —
  // any stale digest/citations are dropped the moment the place changes.
  useEffect(() => {
    if (running) return;
    setDigest(null);
    setCitations([]);
    setLegacyText('');
    setCurrentRunId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaces]);

  // ── Calendar ────────────────────────────────────────────────────────────
  const [calMonth, setCalMonth] = useState(currentMonth());
  const [dayMeetings, setDayMeetings] = useState<Map<string, MeetingView[]>>(
    new Map()
  );
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // Calendar area view switcher + list pagination (10 first, scroll loads more)
  const [calViewTab, setCalViewTab] = useState<'calendar' | 'list'>('calendar');
  const [listLimit, setListLimit] = useState(10);

  // Filtered rows shared by the meetings list + the calendar
  const buildTypeFilter = (types: string[]) => (a: ArtifactRow) => {
    // Empty selection = ALL classifications (mirrors the engine): Texarkana's
    // dated meetings are Public Hearing/Report, not Minutes/Agenda/Transcript
    // — a forced default would silently empty the calendar + list for them.
    if (types.length === 0) return true;
    const t = types;
    const isExact =
      typeof a.meeting_type === 'string' && t.includes(a.meeting_type);
    const isTranscript =
      t.includes('Transcript') &&
      (a.kind === 'audio' ||
        a.kind === 'video' ||
        (a.meeting_type ?? '').toLowerCase().includes('transcript'));
    return isExact || isTranscript;
  };

  // ── Resolve meeting list for the selected places × date range ─────────────
  useEffect(() => {
    if (selectedPlaces.length === 0) {
      setMeetings([]);
      setCapExcluded(0);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({ limit: '200' });
    // NOTE: no workspace_id — the digest is a "places you follow" tool, scoped
    // to the org entitlements (same scope as the Places picker). Dates are
    // unbounded — the calendar is the sole selection mechanism.
    params.set('jurisdiction_ids', selectedPlaces.join(','));
    params.set('sort_by', 'meeting_date');
    if (dateFrom) params.set('meeting_date_min', dateFrom);
    if (dateTo) params.set('meeting_date_max', dateTo);
    apiFetch(`/api/artifacts?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const raw: ArtifactRow[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const rows = raw.filter((a) =>
          selectedPlaces.includes(a.jurisdiction_id ?? '')
        );
        const typeFiltered = rows
          .filter((a) => !!a.meeting_date) // undated artifacts are not meetings
          .filter(buildTypeFilter(meetingTypes));

        const bodies = Array.from(
          new Set(typeFiltered.map(meetingBody).filter((b): b is string => !!b))
        ).sort();
        const withBucket = [UNCLASSIFIED_BODY, ...bodies];
        setBodyOptions(withBucket);
        // Reference-stable update: keep the previous array when nothing was
        // actually dropped. This effect READS governingBodies (its dep) — a
        // fresh array on every fetch would re-trigger this effect and loop
        // the calendar + meeting list forever.
        setGoverningBodies((prev) => {
          const next = prev.filter((b) => withBucket.includes(b));
          if (
            next.length === prev.length &&
            next.every((b, i) => b === prev[i])
          )
            return prev;
          return next;
        });

        const selectedBodyValues = governingBodies.filter(
          (b) => b !== UNCLASSIFIED_BODY
        );
        const wantUnclassified = governingBodies.includes(UNCLASSIFIED_BODY);
        const filtered =
          governingBodies.length === 0
            ? typeFiltered.filter((a) => meetingBody(a) !== null)
            : typeFiltered.filter((a) => {
                const b = meetingBody(a);
                if (b === null) return wantUnclassified;
                return selectedBodyValues.includes(b);
              });

        const byKey = new Map<string, ArtifactRow[]>();
        // Meetings are ANCHORED only by meeting-record classifications —
        // supporting docs (Report/Resolution/Memo…) join an anchored meeting
        // on the same key but never create one (parity with the engine).
        const isMeetingRecord = (a: ArtifactRow): boolean => {
          const t = (a.meeting_type ?? '').trim();
          if (MEETING_RECORD_TYPES.includes(t)) return true;
          return a.kind === 'audio' || a.kind === 'video';
        };
        const records = filtered.filter(isMeetingRecord);
        for (const a of records) {
          const k = meetingKey(
            a.jurisdiction_id!,
            meetingBody(a),
            String(a.meeting_date ?? '').slice(0, 10)
          );
          const arr = byKey.get(k) ?? [];
          arr.push(a);
          byKey.set(k, arr);
        }
        for (const a of filtered) {
          if (isMeetingRecord(a)) continue;
          const k = meetingKey(
            a.jurisdiction_id!,
            meetingBody(a),
            String(a.meeting_date ?? '').slice(0, 10)
          );
          if (!byKey.has(k)) continue;
          byKey.get(k)!.push(a);
        }
        const sorted = [...byKey.entries()]
          .map(([key, docs]) => ({
            key,
            jurisdictionId: docs[0].jurisdiction_id!,
            jurisdictionName: placeName(docs[0].jurisdiction_id!),
            governingBody: meetingBody(docs[0]),
            meetingDate: docs[0].meeting_date!,
            types: Array.from(
              new Set(docs.map((d) => d.meeting_type ?? d.kind ?? ''))
            ).filter(Boolean),
          }))
          .sort((a, b) =>
            a.meetingDate < b.meetingDate
              ? 1
              : a.meetingDate > b.meetingDate
                ? -1
                : 0
          );
        const cap = Number(
          (tool.config as Record<string, unknown> | null | undefined)
            ?.digest_cap ?? 25
        );
        const capped = sorted.slice(0, cap);
        setCapExcluded(Math.max(0, sorted.length - cap));
        setMeetings(capped);
        setCheckedMeetings(new Set(capped.map((m) => m.key)));
        setSelectedViews(new Map(capped.map((m) => [m.key, m])));
        setListLimit(10);

        // ── Calendar dots = THE SAME meeting set the summary uses ──────────
        // One source of truth: every matched meeting (pre-cap — the excluded
        // few are still visible on the calendar) keys its meeting date, so
        // highlights, the header count and the 'too many meetings' warning
        // can never disagree, and no separate month fetch can race or go
        // stale. Month navigation just changes which cells are shown.
        const dayMap = new Map<string, MeetingView[]>();
        for (const m of sorted) {
          const arr = dayMap.get(m.meetingDate) ?? [];
          arr.push(m);
          dayMap.set(m.meetingDate, arr);
        }
        setDayMeetings(dayMap);
      })
      .catch(() => {
        if (!cancelled) setMeetings([]);
      })
      .finally(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaces, dateFrom, dateTo, meetingTypes, governingBodies, places]);

  // ── Calendar navigation (single source of truth: dayMeetings IS the
  // resolution's matched meeting set — see the resolution effect) ──────────
  const prevPlacesRef = useRef<string>('');
  const autoJumpedRef = useRef(false);

  // ── URL state sync (debug-friendly) ──────────────────────────────────────
  // The meaningful tool state is mirrored to the query string via
  // history.replaceState (no reloads), so any view is reproducible/debuggable
  // by copying the URL: view, place, month, filters, run.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setP = (k: string, v?: string | string[] | null) => {
      if (v == null || (Array.isArray(v) && v.length === 0) || v === '') {
        params.delete(k);
        return;
      }
      params.set(k, Array.isArray(v) ? v.join(',') : String(v));
    };
    setP('view', view);
    setP('place', selectedPlaces[0] ?? null);
    setP('month', calMonth);
    setP('mt', meetingTypes);
    setP('gb', governingBodies);
    setP('at', analysisTypes);
    setP('run', currentRunId);
    const qs = params.toString();
    window.history.replaceState(
      null,
      '',
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    );
  }, [
    view,
    selectedPlaces,
    calMonth,
    meetingTypes,
    governingBodies,
    analysisTypes,
    currentRunId,
  ]);

  // One-time init from the URL — lets a copied URL reproduce the view.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const viewP = p.get('view');
    if (viewP === 'result') setView('result');
    else if (viewP === 'create') setView('create');
    const place = p.get('place');
    if (place && /^[0-9a-f-]{36}$/i.test(place)) {
      setSelectedPlaces([place]);
      // Pre-arm the place-change guard so it doesn't reset the URL month.
      prevPlacesRef.current = place;
    }
    const month = p.get('month');
    if (month && /^\d{4}-\d{2}$/.test(month)) setCalMonth(month);
    const mt = (p.get('mt') ?? '').split(',').filter(Boolean);
    if (mt.length) setMeetingTypes(mt);
    const gb = (p.get('gb') ?? '').split(',').filter(Boolean);
    if (gb.length) setGoverningBodies(gb);
    const at = (p.get('at') ?? '').split(',').filter(Boolean);
    if (at.length) setAnalysisTypes(at);
  }, []);

  // On a new place selection: reset the once-per-place auto-jump guard and
  // return to the local current month. The resolution effect re-derives
  // dayMeetings whenever the selection changes.
  useEffect(() => {
    const placesKey = [...selectedPlaces].sort().join(',');
    if (placesKey !== prevPlacesRef.current) {
      prevPlacesRef.current = placesKey;
      autoJumpedRef.current = false;
      setCalMonth(currentMonth());
    }
  }, [selectedPlaces]);

  // Auto-jump ONCE per place selection: when the visible month is empty but
  // the meeting set has dots elsewhere (e.g. Hokah's latest meeting is in
  // June), hop to the LATEST month with meetings so the user sees highlight
  // candidates immediately. Manual month navigation disables the jump.
  useEffect(() => {
    const keys = [...dayMeetings.keys()];
    if (keys.length === 0) return;
    const latest = keys.slice().sort()[keys.length - 1] ?? '';
    const latestMonth = latest.slice(0, 7);
    const hasVisible = keys.some((k) => k.startsWith(calMonth + '-'));
    if (!hasVisible && !autoJumpedRef.current) {
      autoJumpedRef.current = true;
      setCalMonth(latestMonth);
    }
  }, [calMonth, dayMeetings, selectedPlaces]);

  // ── Run digest ─────────────────────────────────────────────────────────────
  const handleRun = async () => {
    if (selectedPlaces.length === 0) {
      setRunError('Select at least one place.');
      return;
    }
    if (checkedMeetings.size === 0) {
      setRunError('No meetings selected — check at least one meeting.');
      return;
    }
    setRunning(true);
    setRunError(null);
    setQueuedCount(checkedMeetings.size);
    setElapsedSec(0);
    // Move straight to the Meeting Digest chat window — the summary renders
    // there (with a generating state) and the chat is ready right after.
    setView('result');
    const ctrl = new AbortController();
    const ctrlTimer = setTimeout(() => ctrl.abort(), 290_000); // just under nginx 300s
    try {
      const res = await apiFetch('/api/meeting-digest/run', {
        method: 'POST',
        signal: ctrl.signal,
        body: JSON.stringify({
          tool_id: tool.id,
          place_ids: selectedPlaces,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          meeting_types: meetingTypes,
          governing_bodies: governingBodies.filter(
            (b) => b !== UNCLASSIFIED_BODY
          ),
          include_unclassified: governingBodies.includes(UNCLASSIFIED_BODY),
          meeting_keys: [...checkedMeetings],
          analysis_types: analysisTypes,
        }),
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ error: 'Digest run failed' }));
        setRunError(err.error ?? 'Digest run failed');
        return;
      }
      const body = await res.json();
      setDigest(body?.data?.digest ?? null);
      setCitations(body?.data?.citations ?? []);
      setLegacyText('');
      setCurrentRunId(body?.data?.runId ?? null);
      void ctrlTimer;
      setCapExcluded(body?.data?.excludedCount ?? 0);
      setView('result');
      loadRuns();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setRunError(
          'Generation timed out after ~5 minutes. Try fewer meetings or a narrower filter, then Generate again.'
        );
      } else {
        setRunError(
          'Digest run failed — the backend may be busy. Try again, or use fewer meetings.'
        );
      }
    } finally {
      clearTimeout(ctrlTimer);
      setRunning(false);
    }
  };

  // Calendar grid for the visible month
  const calCells = useMemo(() => {
    const { start } = monthBounds(calMonth);
    const [y, m] = calMonth.split('-').map(Number);
    const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const cells: Array<{ date: string; day: number; inMonth: boolean }> = [];
    for (let i = 0; i < firstDow; i++)
      cells.push({ date: '', day: 0, inMonth: false });
    for (let d = 1; d <= last; d++) {
      const date = `${calMonth}-${String(d).padStart(2, '0')}`;
      cells.push({ date, day: d, inMonth: true });
    }
    void start;
    return cells;
  }, [calMonth]);

  /**
   * The count displayed in the calendar header is ALWAYS derived from the
   * grid itself (month-scoped cells with meetings), never from the raw
   * dayMeetings map size — a stale/ghost key from another month or place can
   * inflate size but can never count or highlight here.
   */
  const visibleMeetingDays = useMemo(() => {
    const prefix = `${calMonth}-`;
    return calCells.filter(
      (c) =>
        c.inMonth &&
        c.date.startsWith(prefix) &&
        (dayMeetings.get(c.date)?.length ?? 0) > 0
    ).length;
  }, [calCells, dayMeetings, calMonth]);

  /** Reset the New Digest form completely — no previously selected options or dates. */
  const openFreshCreate = () => {
    setSelectedPlaces([]);
    setDateFrom('');
    setDateTo('');
    setCurrentRunId(null);
    setMeetingTypes([]);
    setGoverningBodies([]);
    setAnalysisTypes(['summary']);
    setCapExcluded(0);
    setCheckedMeetings(new Set());
    setSelectedViews(new Map());
    setRunError(null);
    setOpenDay(null);
    setSelectedDay(null);
    setDayMeetings(new Map());
    autoJumpedRef.current = false;
    prevPlacesRef.current = '';
    setView('create');
  };

  /** Check every available meeting (the resolution's capped set). */
  const selectAllMeetings = () => {
    setCheckedMeetings(new Set(meetings.map((m) => m.key)));
    setSelectedViews(new Map(meetings.map((m) => [m.key, m])));
  };

  /** Uncheck everything. */
  const selectNoneMeetings = () => {
    setCheckedMeetings(new Set());
    setSelectedViews(new Map());
  };

  const toggleMeeting = (key: string) => {
    const has = checkedMeetings.has(key);
    setCheckedMeetings((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSelectedViews((prev) => {
      const next = new Map(prev);
      if (has) {
        next.delete(key);
      } else {
        const found =
          meetings.find((m) => m.key === key) ??
          [...dayMeetings.values()].flat().find((m) => m.key === key);
        if (found) next.set(key, found);
      }
      return next;
    });
  };

  return (
    <div className="space-y-5">
      {/* ── Header (mirrors Document Q&A / On the Record; the result screen shows
           the Meeting DIGEST header instead of the tool header) ── */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <button
            type="button"
            onClick={() => navigate('/tools')}
            className="text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
          >
            Tools
          </button>
          {view !== 'history' && (
            <>
              <span className="text-gray-400 text-sm">/</span>
              <button
                type="button"
                onClick={() => setView('history')}
                className="text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
                title="Meeting Summarizer landing page"
              >
                Meeting Summarizer
              </button>
            </>
          )}
        </div>
        {view === 'result' ? (
          <h1 className="text-3xl font-bold text-gray-900">Meeting Digest</h1>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-gray-900">
              Meeting Summarizer
            </h1>
            <p className="mt-1 text-gray-900">
              Catch up on the places you follow: choose a place, pick meetings
              on the calendar, and get a structured summary of every meeting —
              what was discussed, what was decided, and how each vote came out,
              all linked back to the source document.
            </p>
          </>
        )}
      </div>

      {/* ── HISTORY VIEW (Landing) ─────────────────────────────────────── */}
      {view === 'history' && (
        <div className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Meeting Summary Searches
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Catch up on what happened across the places you follow.
              </p>
            </div>
            <Button size="sm" onClick={openFreshCreate}>
              <PlusCircle className="h-4 w-4 mr-1" /> New Meeting Summary
            </Button>
          </div>

          {/* Criteria chip strip from the last run */}
          {lastRun && (
            <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                const rs = lastRun.input?.run_scope;
                if (!rs) return null;
                const placeNames = (rs.place_ids ?? []).map((id) => {
                  if (rs.jurisdiction_names?.[id])
                    return rs.jurisdiction_names[id];
                  const p = basePlaces.find((b) => b.id === id);
                  return p?.name ?? id.slice(0, 8);
                });
                return (
                  <>
                    <span className="text-xs text-gray-400 uppercase tracking-wide">
                      Last digest
                    </span>
                    {rs.start_date && rs.end_date && (
                      <Chip
                        label={`${fmtDate(rs.start_date)} – ${fmtDate(rs.end_date)}`}
                        onClick={() => prefillFromRun(lastRun)}
                      />
                    )}
                    {placeNames.slice(0, 4).map((n) => (
                      <Chip
                        key={n}
                        label={n}
                        onClick={() => prefillFromRun(lastRun)}
                      />
                    ))}
                    {placeNames.length > 4 && (
                      <Chip label={`+${placeNames.length - 4} more`} />
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {runsLoading && (
            <div className="py-10 text-center text-sm text-gray-400">
              Loading digests…
            </div>
          )}
          {!runsLoading && historyError && (
            <div className="py-8 text-center text-sm text-red-600">
              {historyError}
            </div>
          )}
          {!runsLoading && !historyError && runs.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="text-3xl mb-2">🗓️</div>
                <p className="text-sm text-gray-500 mb-3">
                  No digests yet — generate your first one to see it here.
                </p>
                <Button size="sm" onClick={openFreshCreate}>
                  <PlusCircle className="h-4 w-4 mr-1" /> Create your first
                  summary
                </Button>
              </CardContent>
            </Card>
          )}
          {!runsLoading &&
            runs.map((run) => {
              const parsed = parseDigestBlock(run);
              const text = runTextBlock(run);
              const viewable = run.status === 'succeeded' && (parsed || text);
              const meetingCount = parsed?.coverage?.included_meetings ?? 0;
              const rs = run.input?.run_scope;
              return (
                <Card
                  key={run.id}
                  className={`rounded-2xl border-gray-200 shadow-sm ${
                    viewable
                      ? 'hover:border-indigo-200 hover:shadow transition-all'
                      : 'opacity-70'
                  }`}
                >
                  <CardContent className="py-4 flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {meetingCount > 0 && (
                          <Badge
                            variant="outline"
                            className="bg-indigo-50 text-indigo-700 border-indigo-200"
                          >
                            {meetingCount} meeting
                            {meetingCount !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={
                            run.status === 'succeeded'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }
                        >
                          {run.status}
                        </Badge>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {summaryTitle(run, placeMetaByName)}
                      </p>
                      {rs?.start_date && rs.end_date && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {fmtDate(rs.start_date)} → {fmtDate(rs.end_date)}
                          {typeof run.latency_ms === 'number'
                            ? ` · ${Math.round(run.latency_ms / 1000)}s`
                            : ''}
                          {' · '}
                          {fmtDate(run.created_at)}
                        </p>
                      )}
                    </div>
                    {/* Delete this summary */}
                    <button
                      type="button"
                      title="Delete this summary"
                      onClick={() => handleDeleteRun(run.id)}
                      className="p-2 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!viewable}
                      onClick={() => {
                        if (parsed) {
                          setDigest(parsed);
                          setLegacyText('');
                        } else if (text) {
                          setLegacyText(text);
                          setDigest(null);
                        }
                        setCitations([]);
                        setCurrentRunId(run.id);
                        setView('result');
                      }}
                    >
                      View <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </CardContent>
                  {viewable && (
                    <div className="border-t border-gray-100 px-4 py-2">
                      <MessageActions
                        toolRunId={run.id}
                        reasonsDown={summaryReasons.down}
                        reasonsUp={summaryReasons.up}
                        initialRating={summaryVotes.get(run.id)}
                        onFeedback={({ rating, reasonCode, comment }) =>
                          submitSummaryFeedback(
                            run.id,
                            rating,
                            reasonCode,
                            comment
                          )
                        }
                      />
                    </div>
                  )}
                </Card>
              );
            })}
        </div>
      )}

      {/* ── CREATE VIEW (New Digest) ───────────────────────────────────── */}
      {view === 'create' && (
        <div className="grid grid-cols-3 gap-x-5 gap-y-2 items-start">
          {/* Left — Places, Calendar span 2 columns */}
          <div className="col-span-2 min-w-0 space-y-2">
            <Card className="rounded-2xl border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-500" /> Places
                  <span className="text-xs font-normal text-gray-400 ml-auto">
                    {selectedPlaces.length > 0 ? 'Selected' : 'Required'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* Searchable dropdown — one place at a time */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setPlacesOpen((v) => !v)}
                    className={`w-full h-10 text-sm rounded-xl border px-3 flex items-center justify-between transition-colors ${
                      placesOpen
                        ? 'border-indigo-400 ring-2 ring-indigo-100'
                        : 'border-gray-300 hover:border-gray-400'
                    } ${selectedPlaces.length > 0 ? 'bg-indigo-50/50' : 'bg-white'}`}
                  >
                    <span className="truncate">
                      {selectedPlaces.length > 0 ? (
                        <>
                          <span className="font-medium text-gray-900">
                            {placeName(selectedPlaces[0])}
                          </span>
                          {(() => {
                            const p = places.find(
                              (x) => x.id === selectedPlaces[0]
                            );
                            if (!p?.level) return null;
                            return (
                              <span className="text-gray-400">
                                {' '}
                                ({typeLabel(p.level)})
                              </span>
                            );
                          })()}
                        </>
                      ) : (
                        <span className="text-gray-400">Select a place…</span>
                      )}
                    </span>
                    <ChevronRight
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        placesOpen ? 'rotate-90' : ''
                      }`}
                    />
                  </button>

                  {placesOpen && (
                    <div className="absolute left-0 right-0 top-11 z-20 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                      <div className="relative p-2 bg-gray-50 border-b border-gray-100">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          value={placeFilter}
                          onChange={(e) => setPlaceFilter(e.target.value)}
                          placeholder="Search places — e.g. jersey, hudson county…"
                          className="pl-9 text-sm bg-white"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                        {placesError && (
                          <p className="px-3 py-3 text-sm text-red-600">
                            {placesError}
                          </p>
                        )}
                        {!placesError && placesLoading && (
                          <p className="px-3 py-3 text-sm text-gray-400">
                            Loading your places…
                          </p>
                        )}
                        {!placesError &&
                          !placesLoading &&
                          places.length === 0 && (
                            <p className="px-3 py-3 text-sm text-gray-400">
                              No places available in your access scope.
                            </p>
                          )}
                        {!placesLoading &&
                          places.map((p) => {
                            const checked = selectedPlaces[0] === p.id;
                            const ancestors = p.path.slice(0, -1);
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => {
                                  setSelectedPlaces(checked ? [] : [p.id]);
                                  setPlaceFilter('');
                                  setPlacesOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-gray-50 transition-colors ${
                                  checked ? 'bg-indigo-50/60' : ''
                                }`}
                              >
                                <span
                                  className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors ${
                                    checked
                                      ? 'border-indigo-600'
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {checked && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-600" />
                                  )}
                                </span>
                                <span className="min-w-0">
                                  <span className="block truncate text-gray-900">
                                    {p.name}
                                    {p.level ? (
                                      <span className="text-gray-400">
                                        {' '}
                                        ({typeLabel(p.level)})
                                      </span>
                                    ) : null}
                                  </span>
                                  {ancestors.length > 0 && (
                                    <span className="block text-xs text-gray-400 truncate">
                                      {ancestors.join(' \u2192 ')}
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 w-full">
                  <Calendar className="h-4 w-4 text-indigo-500" /> Calendar
                  <span className="ml-auto inline-flex items-center rounded-full bg-gray-100 p-0.5">
                    {(
                      [
                        { key: 'calendar', label: 'Calendar View' },
                        { key: 'list', label: 'List View' },
                      ] as const
                    ).map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setCalViewTab(t.key)}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
                          calViewTab === t.key
                            ? 'bg-white text-indigo-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-800'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedPlaces.length === 0 ? (
                  <p className="text-sm text-gray-400 py-3 text-center">
                    Choose at least one place to see meetings.
                  </p>
                ) : calViewTab === 'list' ? (
                  /* ── LIST VIEW: 2 columns (Date | Meeting title), 10 first,
                     infinite scroll loads more as the user scrolls ── */
                  <div
                    className="border rounded-xl divide-y divide-gray-50 max-h-[26rem] overflow-y-auto bg-white"
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (
                        el.scrollTop + el.clientHeight >=
                          el.scrollHeight - 48 &&
                        listLimit < meetings.length
                      ) {
                        setListLimit((n) => Math.min(n + 10, meetings.length));
                      }
                    }}
                  >
                    <div className="px-3 py-2 flex items-center gap-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide sticky top-0 bg-white border-b border-gray-100">
                      <span className="w-4 flex-shrink-0" />
                      <span className="w-28 flex-shrink-0">Date</span>
                      <span>Meeting</span>
                    </div>
                    {meetings.length === 0 && (
                      <p className="px-3 py-6 text-center text-sm text-gray-400">
                        No meetings found for this place and current filters.
                      </p>
                    )}
                    {meetings.slice(0, listLimit).map((m) => {
                      const checked = checkedMeetings.has(m.key);
                      return (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => toggleMeeting(m.key)}
                          className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors ${
                            checked ? 'bg-indigo-50/50' : ''
                          }`}
                        >
                          <span
                            className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                              checked
                                ? 'bg-indigo-600 border-indigo-600'
                                : 'border-gray-300'
                            }`}
                          >
                            {checked && (
                              <Check className="h-2.5 w-2.5 text-white" />
                            )}
                          </span>
                          <span className="w-28 flex-shrink-0 text-sm text-black">
                            {fmtDate(m.meetingDate)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-black font-medium">
                              {m.jurisdictionName} —{' '}
                              {m.governingBody ?? 'Unclassified body'}
                            </span>
                            {m.types.length > 0 && (
                              <span className="flex items-center gap-1 mt-0.5 flex-wrap">
                                {m.types.map((t) => (
                                  <span
                                    key={t}
                                    className="text-[10px] font-medium bg-gray-100 border border-gray-200 text-gray-600 rounded-full px-1.5"
                                  >
                                    {t}
                                  </span>
                                ))}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                    {listLimit < meetings.length && (
                      <p className="px-3 py-2 text-center text-xs text-gray-400">
                        Scroll for more ({listLimit} of {meetings.length})
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Month nav */}
                    <div className="flex items-center justify-between mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          autoJumpedRef.current = true;
                          setCalMonth(shiftMonth(calMonth, -1));
                        }}
                        className="p-1 rounded-full hover:bg-gray-100"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-semibold text-gray-900">
                        {(() => {
                          // Local-safe: 'YYYY-MM-01' parsed via new Date(…) is UTC
                          // midnight → the header can render the PREVIOUS month
                          // in western timezones. Build a local noon Date instead.
                          const [yy, mm] = calMonth.split('-').map(Number);
                          return new Date(
                            yy,
                            mm - 1,
                            1,
                            12,
                            0,
                            0
                          ).toLocaleDateString(undefined, {
                            month: 'long',
                            year: 'numeric',
                          });
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          autoJumpedRef.current = true;
                          setCalMonth(shiftMonth(calMonth, 1));
                        }}
                        className="p-1 rounded-full hover:bg-gray-100"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      <span className="text-[11px] text-gray-400">
                        {visibleMeetingDays} meeting day(s)
                      </span>
                    </div>
                    <div className="relative">
                      {/* Day-of-week header */}
                      <div className="grid grid-cols-7 gap-1.5 mb-1">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                          <span
                            key={d}
                            className="text-center text-[10px] font-medium text-gray-400"
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1.5">
                        {calCells.map((c, i) => {
                          if (!c.inMonth)
                            return <span key={i} className="h-8" />;
                          const dayMeets = dayMeetings.get(c.date);
                          const hasMeetings = !!dayMeets?.length;
                          const isToday = c.date === isoToday();
                          const isSelected = c.date === selectedDay;
                          const dayCount = dayMeets?.length ?? 0;
                          return (
                            <button
                              key={c.date}
                              type="button"
                              disabled={!hasMeetings}
                              onClick={() => {
                                const next = openDay === c.date ? null : c.date;
                                setOpenDay(next);
                                setSelectedDay(next);
                              }}
                              // Inline style guarantees the highlight renders
                              // regardless of any stylesheet edge case.
                              style={
                                hasMeetings
                                  ? {
                                      backgroundColor: '#4f46e5',
                                      color: '#ffffff',
                                    }
                                  : undefined
                              }
                              className={`relative h-11 rounded-xl text-sm flex items-center justify-center transition-colors ${
                                hasMeetings
                                  ? 'font-bold hover:opacity-90'
                                  : 'text-gray-400'
                              } ${
                                isSelected
                                  ? 'ring-2 ring-indigo-900 ring-offset-1 ring-offset-white'
                                  : isToday
                                    ? 'ring-2 ring-indigo-300'
                                    : ''
                              }`}
                            >
                              {c.day}
                              {hasMeetings && (
                                <span className="absolute bottom-0.5 right-1 text-[9px] font-semibold opacity-90">
                                  {dayCount > 1 ? `\u00b7${dayCount}` : ''}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-2">
                        Filled days have meetings — click a day to select and
                        review its meetings.
                      </p>
                      {capExcluded > 0 && (
                        <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-1">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                          <span>
                            That&apos;s a lot for one digest — this run covers
                            the {meetings.length} most recent meetings;{' '}
                            {capExcluded} older meeting
                            {capExcluded > 1 ? 's' : ''} will be left out.
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right rail — Meeting Selection, Filters, Alerts, Generate */}
          <div className="col-span-1 min-w-0 space-y-2">
            <Card className="rounded-2xl border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-green-500" /> Meeting
                  Selection
                  <span className="text-xs font-normal text-gray-400 ml-auto">
                    {selectedViews.size > 0
                      ? `${selectedViews.size} selected`
                      : 'Pick from the calendar'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {meetings.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllMeetings}
                      className="px-3 py-1 text-xs font-medium rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={selectNoneMeetings}
                      className="px-3 py-1 text-xs font-medium rounded-full border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                    >
                      Select none
                    </button>
                    <span className="text-xs text-gray-400 ml-auto">
                      {meetings.length} available
                    </span>
                  </div>
                )}
                {selectedViews.size === 0 ? (
                  <p className="text-sm text-gray-400 py-2 text-center">
                    No meetings selected — click a highlighted day on the
                    calendar or use Select all.
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto divide-y divide-gray-50 border rounded-xl">
                    {[...selectedViews.values()]
                      .sort((a, b) =>
                        a.meetingDate < b.meetingDate
                          ? 1
                          : a.meetingDate > b.meetingDate
                            ? -1
                            : 0
                      )
                      .map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => toggleMeeting(m.key)}
                          className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                        >
                          <span className="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center bg-indigo-600 border-indigo-600">
                            <Check className="h-2.5 w-2.5 text-white" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-gray-900">
                              {m.jurisdictionName} —{' '}
                              {m.governingBody ?? 'Unclassified body'}
                            </span>
                            <span className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className="text-xs text-gray-500">
                                {fmtDate(m.meetingDate)}
                              </span>
                              {m.types.map((t) => (
                                <span
                                  key={t}
                                  className="text-[10px] font-medium bg-gray-100 border border-gray-200 text-gray-600 rounded-full px-1.5"
                                >
                                  {t}
                                </span>
                              ))}
                            </span>
                          </span>
                        </button>
                      ))}
                  </div>
                )}
                {selectedViews.size > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1.5">
                    Click a row to remove it from the summary.
                  </p>
                )}
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Filters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">
                    Meeting type
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {['Minutes', 'Agenda', 'Transcript'].map((t) => {
                      const on = meetingTypes.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setMeetingTypes((prev) =>
                              on ? prev.filter((x) => x !== t) : [...prev, t]
                            )
                          }
                          className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                            on || meetingTypes.length === 0
                              ? on
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {t}
                          {meetingTypes.length === 0 && t === 'Minutes' && (
                            <span className="text-gray-400 ml-1">(all)</span>
                          )}
                        </button>
                      );
                    })}
                    {meetingTypes.length === 0 && (
                      <span className="text-xs text-gray-400">
                        all types selected
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">
                    Governing body
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap max-h-24 overflow-y-auto">
                    {bodyOptions.map((b) => {
                      const label =
                        b === UNCLASSIFIED_BODY ? 'Unclassified body' : b;
                      const on = governingBodies.includes(b);
                      return (
                        <button
                          key={b}
                          type="button"
                          onClick={() =>
                            setGoverningBodies((prev) =>
                              on ? prev.filter((x) => x !== b) : [...prev, b]
                            )
                          }
                          className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                            on
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                    {bodyOptions.length === 0 && (
                      <span className="text-xs text-gray-400">
                        Select places + a date range to see governing bodies.
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">
                    Analysis type
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(
                      ['summary', 'agenda_items', 'motions_votes'] as const
                    ).map((t) => {
                      const on = analysisTypes.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setAnalysisTypes((prev) =>
                              on ? prev.filter((x) => x !== t) : [...prev, t]
                            )
                          }
                          className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                            on
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {t === 'summary'
                            ? 'Summary'
                            : t === 'agenda_items'
                              ? 'Agenda items'
                              : 'Motions & votes'}
                        </button>
                      );
                    })}
                    {analysisTypes.length === 0 && (
                      <span className="text-xs text-amber-600">
                        Select at least one analysis type.
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {runError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                {runError}
              </div>
            )}

            <Button
              disabled={
                running ||
                selectedPlaces.length === 0 ||
                analysisTypes.length === 0 ||
                checkedMeetings.size === 0
              }
              onClick={handleRun}
              className="w-full flex items-center justify-center gap-1 rounded-xl"
            >
              {running ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  Generating summary…
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4" />
                  Generate Summary
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ── RESULT VIEW ─────────────────────────────────────────────────── */}
      {view === 'result' && (
        <ResultView
          digest={digest}
          citations={citations}
          legacyText={legacyText}
          placeMetaByName={placeMetaByName}
          digestText={digest ? JSON.stringify(digest, null, 2) : legacyText}
          running={running}
          runError={runError}
          currentRunId={currentRunId}
          onDeleteCurrent={handleDeleteCurrent}
          elapsedSec={elapsedSec}
          queuedCount={queuedCount}
        />
      )}

      {/* Meeting day overlay — list of meetings on the clicked date */}
      {openDay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 print-hide"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenDay(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md divide-y divide-gray-100 max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-3 flex items-center justify-between sticky top-0 bg-white border-b border-gray-100 z-10">
              <span className="text-sm font-semibold text-gray-900">
                Meetings on {fmtDate(openDay)}
              </span>
              <button
                type="button"
                onClick={() => setOpenDay(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {(dayMeetings.get(openDay) ?? []).map((m) => {
              const checked = checkedMeetings.has(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => toggleMeeting(m.key)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                    checked ? 'bg-indigo-50/50' : ''
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                      checked
                        ? 'bg-indigo-600 border-indigo-600'
                        : 'border-gray-300'
                    }`}
                  >
                    {checked && <Check className="h-2.5 w-2.5 text-white" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-gray-900">
                      {m.jurisdictionName} —{' '}
                      {m.governingBody ?? 'Unclassified body'}
                    </span>
                    <span className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {m.types.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] font-medium bg-gray-100 border border-gray-200 text-gray-600 rounded-full px-1.5"
                        >
                          {t}
                        </span>
                      ))}
                    </span>
                  </span>
                </button>
              );
            })}
            <div className="px-4 py-2 text-xs text-gray-400">
              Checked meetings are included in the digest.
            </div>
          </div>
        </div>
      )}

      {/* ── Soft-hide confirmation modal ─────────────────────────────── */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 print-hide"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setPendingDelete(null);
              setConfirmChecked(false);
            }}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Remove this summary?
            </h3>
            <p className="text-sm text-gray-700">
              This removes the summary from your list of summaries. It stays in
              the database and remains recoverable by an administrator.
            </p>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                className="mt-0.5 accent-red-600"
              />
              <span className="text-sm text-gray-800">
                I understand — remove it from my summaries list (it is not
                deleted from the database).
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPendingDelete(null);
                  setConfirmChecked(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!confirmChecked}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                onClick={() => void confirmHideDelete()}
              >
                Remove from my summaries
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Print styles — hide app chrome, full-width digest */}
      <style>{`
        /* ── SOP report typography (screen + print) ── */
        .report-title { font-size: 24px; font-weight: 700; color: #000; margin: 0 0 6px; }
        .report-masthead { margin-top: 2px; }
        .report-title-tight { margin-top: 0; }
        .report-sub { font-size: 15px; color: #000; margin: 0 0 6px; }
        .report-meta { font-size: 12px; color: #444; margin: 0; }
        .report-meta-block { margin-top: 4px; }
        .report-rule { border: none; border-top: 1.5px solid #000; margin: 16px 0 24px; }
        .report-h2 {
          font-size: 20px; font-weight: 700; color: #000;
          margin: 28px 0 12px;
        }
        .report-h3 { font-size: 18px; font-weight: 700; color: #000; margin: 24px 0 8px; }
        .report-h3-meta { font-size: 13px; font-weight: 500; color: #444; }
        .report-h4 { font-size: 15px; font-weight: 700; color: #000; margin: 16px 0 6px; }
        .report-label {
          font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #000; margin: 12px 0 4px;
        }
        .report-p { font-size: 14px; line-height: 1.6; color: #000; margin: 0 0 10px; max-width: 70ch; }
        .report-list, .report-ol {
          list-style-type: disc; list-style-position: outside;
          padding-left: 1.4em; margin: 0 0 10px;
          font-size: 14px; line-height: 1.6; color: #000;
        }
        .report-ol { list-style-type: decimal; }
        .report-list > li, .report-ol > li { margin: 4px 0; }
        .report-link { color: #000; text-decoration: underline; }
        .report-place { margin-top: 8px; }
        .report-meeting-list { margin-top: 6px; }

        @media print {
          .print-hide { display: none !important; }
          @page { size: Letter; margin: 0.6in 0.75in; }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .report-place { page-break-before: always; }
          .report-section, .report-meeting, .report-subsection { break-inside: avoid; page-break-inside: avoid; }
          .report-list > li, .report-ol > li { break-inside: avoid; page-break-inside: avoid; }
          /* Monochrome SOP output — plain black on white */
          .report-sheet * {
            color: #000 !important;
            background: transparent !important;
            box-shadow: none !important;
          }
          .report-rule { border-top-color: #000 !important; }
        }
      `}</style>
    </div>
  );
};

// ── Follow-up chat (general chat window for the generated digest) ───────────

function DigestChat({
  digestText,
  citations,
}: {
  digestText: string;
  citations: Citation[];
}) {
  const [messages, setMessages] = useState<
    Array<{ role: 'user' | 'assistant'; content: string; runId?: string }>
  >([]);
  const [digestReasons, setDigestReasons] = useState<{
    up: { code: string; label: string }[];
    down: { code: string; label: string }[];
  }>({ up: [], down: [] });

  useEffect(() => {
    apiFetch('/api/feedback/reasons?tool_slug=meeting-summary')
      .then((r) => r.json())
      .then(
        (d: {
          data?: Array<{ code: string; label: string; polarity: string }>;
        }) => {
          setDigestReasons({
            up: (d?.data ?? []).filter((r) => r.polarity === 'up'),
            down: (d?.data ?? []).filter((r) => r.polarity === 'down'),
          });
        }
      )
      .catch(() => {
        /* non-critical */
      });
  }, []);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attach, setAttach] = useState<{ name: string; text: string } | null>(
    null
  );
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const sessionId = useRef<string>(crypto.randomUUID());
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  // Auto-scroll: keep the newest message in view like the normal chat.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const system = () => {
    const sources = citations
      .map(
        (c) =>
          `- Source: ${c.artifactTitle ?? c.artifactId ?? ''}${c.artifactUrl ? ` (${c.artifactUrl})` : ''}`
      )
      .join('\n');
    let sys =
      'You are the Meeting Summarizer chat assistant. A meeting digest was ' +
      'just generated and is factual reference material. Answer follow-up ' +
      'questions strictly from the digest and any attached document text; do ' +
      'not invent facts, dates, names or amounts. If the digest does not cover ' +
      'a question, say so plainly and, when useful, point to the source ' +
      'documents or suggest a narrower question. Keep answers concise and ' +
      'structured like the digest.\n\nMEETING DIGEST:\n' +
      digestText.slice(0, 60000);
    if (sources) sys += '\n\nSOURCES CITED IN THE DIGEST:\n' + sources;
    if (attach) {
      sys += `\n\nATTACHED DOCUMENT "${attach.name}" (uploaded this session):\n${attach.text.slice(0, 40000)}`;
    }
    return sys;
  };

  const submitDigestFeedback = async (
    toolRunId: string,
    rating: 'up' | 'down',
    reasonCode?: string,
    comment?: string
  ) => {
    try {
      const res = await apiFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          tool_run_id: toolRunId,
          rating,
          ...(reasonCode ? { reason_code: reasonCode } : {}),
          ...(comment ? { comment } : {}),
          // Follow-up chat answers never auto-reprocess the digest — route to admin review.
          allow_auto_action: false,
        }),
      });
      const j = (await res.json().catch(() => null)) as {
        action_triggered?: 'reprocess' | 'admin_review' | null;
      } | null;
      return {
        actionTriggered: (j?.action_triggered ?? null) as
          | 'reprocess'
          | 'admin_review'
          | null,
      };
    } catch {
      return { actionTriggered: null };
    }
  };

  const send = async () => {
    const q = input.trim();
    if (!q || sending || uploading) return;
    setNote(null);
    const next = [...messages, { role: 'user' as const, content: q }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const res = await apiFetch('/api/ai/complete', {
        method: 'POST',
        body: JSON.stringify({
          model_id: 'claude-sonnet-4-6',
          system: system(),
          messages: next.slice(-12),
        }),
      });
      if (!res.ok) throw new Error('completion failed');
      const data = await res.json();
      const content = (data?.data?.content ?? '').trim();
      if (!content) throw new Error('empty completion');
      let runId: string | undefined;
      try {
        const rec = await apiFetch('/api/tool-runs/record-chat', {
          method: 'POST',
          body: JSON.stringify({
            tool_slug: 'meeting-summary',
            session_id: sessionId.current,
            question: q,
            answer: content,
          }),
        });
        const recJson = (await rec.json().catch(() => null)) as {
          data?: { id?: string };
        } | null;
        runId = recJson?.data?.id;
      } catch {
        /* run recording is best-effort — feedback needs runId though */
      }
      setMessages((prev) => [...prev, { role: 'assistant', content, runId }]);
    } catch {
      setNote('The assistant could not respond — please try again.');
      setMessages(next);
    } finally {
      setSending(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setNote(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId.current);
      const up = await apiFetch('/api/artifacts/upload-session', {
        method: 'POST',
        body: formData,
        headers: {},
      });
      if (!up.ok) throw new Error('upload failed');
      const upData = await up.json();
      const artifactId = upData?.artifact_id;
      if (!artifactId) throw new Error('no artifact id');
      // Pull the extracted text so the attached document actually feeds the chat.
      const tx = await apiFetch(`/api/artifacts/${artifactId}/text`);
      if (tx.ok) {
        const txData = await tx.json();
        const text = txData?.data?.text ?? '';
        setAttach({
          name: file.name,
          text: text ? String(text) : '[no extracted text yet]',
        });
      } else {
        setAttach({ name: file.name, text: '[no extracted text yet]' });
      }
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: `[Attached document: ${file.name}]` },
      ]);
    } catch {
      setNote('Could not attach the document — please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col print-hide">
      {/* Chat header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-200">
        <Send className="h-4 w-4 text-indigo-500" />
        <span className="text-sm font-semibold text-gray-900">
          Follow-Up Questions
        </span>
        <span className="text-xs text-gray-400 ml-auto">
          Answers come from this digest
          {citations.length > 0 ? ' + its sources' : ''}
        </span>
      </div>

      {/* Thread — scrolls, newest at the bottom, normal-chat bubbles */}
      <div
        ref={threadRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[300px] max-h-[52vh] bg-gray-50/60"
      >
        {messages.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">
            The summary is ready — ask about a decision, vote, date or source,
            e.g. &ldquo;What was the outcome of the resurfacing item?&rdquo;
          </p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-blue-600 text-white text-[14px] font-medium whitespace-pre-wrap'
                  : 'bg-gray-50 border border-gray-300 text-gray-950'
              }`}
            >
              {m.role === 'assistant' ? (
                <div className={MD_CLASSES}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: (props) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noreferrer noopener"
                        />
                      ),
                    }}
                  >
                    {m.content}
                  </ReactMarkdown>
                </div>
              ) : (
                m.content
              )}
            </div>
            {m.role === 'assistant' && m.runId && (
              <div className="ml-1">
                <MessageActions
                  toolRunId={m.runId}
                  reasonsDown={digestReasons.down}
                  reasonsUp={digestReasons.up}
                  onFeedback={({ rating, reasonCode, comment }) =>
                    submitDigestFeedback(
                      m.runId as string,
                      rating,
                      reasonCode,
                      comment
                    )
                  }
                />
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-gray-50 border border-gray-300 text-sm text-gray-500">
              <Loader2 className="h-3.5 w-3.5 inline animate-spin mr-1.5" />
              Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Attach chip + errors */}
      <div className="px-4 pt-2 space-y-1.5">
        {note && <p className="text-xs text-red-600">{note}</p>}
        {attach && (
          <div className="inline-flex items-center gap-2 text-xs text-gray-600 bg-indigo-50 border border-indigo-200 rounded-full px-3 py-1">
            <Paperclip className="h-3 w-3" />
            <span className="truncate max-w-[260px]">{attach.name}</span>
            <button
              type="button"
              onClick={() => setAttach(null)}
              className="text-gray-400 hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Pinned input row (like the normal chat) */}
      <div className="flex items-end gap-2 px-4 py-3 bg-white border-t border-gray-200">
        <label
          className={`flex-shrink-0 p-2 rounded-xl border cursor-pointer transition-colors ${
            uploading
              ? 'bg-gray-100 border-gray-200 text-gray-400'
              : 'border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
          title="Attach a document"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Paperclip className="h-4 w-4" />
          )}
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
              e.target.value = '';
            }}
          />
        </label>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask a follow-up about the digest… (Enter to send)"
          rows={2}
          className="flex-1 resize-none text-sm rounded-xl border border-gray-300 px-3 py-2 focus-visible:ring-2 focus-visible:ring-indigo-300 outline-none"
        />
        <Button
          size="sm"
          disabled={!input.trim() || sending || uploading}
          onClick={() => void send()}
          className="flex-shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Journalist Brief (MeetingCite framework block) ───────────────────────────

function JournalistBrief({
  framework,
}: {
  framework: NonNullable<Digest['framework']>;
}) {
  const subs: Array<{
    num: string;
    title: string;
    nodes: React.ReactNode | null;
  }> = [
    {
      num: '2.1',
      title: "What's New",
      nodes: framework.whats_new?.length ? (
        <ul className="report-list">
          {framework.whats_new.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null,
    },
    {
      num: '2.2',
      title: 'What Changed',
      nodes: framework.what_changed?.length ? (
        <ul className="report-list">
          {framework.what_changed.map((c, i) => (
            <li key={i}>
              <span className="font-semibold">{c.issue}</span>
              {c.earlier ? ` — earlier: ${c.earlier}` : ''}
              {c.later ? ` \u00b7 later: ${c.later}` : ''}
              {c.change ? ` \u00b7 change: ${c.change}` : ''}
            </li>
          ))}
        </ul>
      ) : null,
    },
    {
      num: '2.3',
      title: 'Contradictions & Discrepancies',
      nodes: framework.contradictions?.length ? (
        <ul className="report-list">
          {framework.contradictions.map((c, i) => (
            <li key={i}>
              <span className="font-semibold">{c.item}</span>
              {c.documents ? ` (${c.documents})` : ''}
              {c.discrepancy ? ` — ${c.discrepancy}` : ''}
            </li>
          ))}
        </ul>
      ) : null,
    },
    {
      num: '2.4',
      title: 'Verification Needed',
      nodes: framework.verification_needed?.length ? (
        <ul className="report-list">
          {framework.verification_needed.map((v, i) => (
            <li key={i}>{v}</li>
          ))}
        </ul>
      ) : null,
    },
    {
      num: '2.5',
      title: 'Reporting Leads',
      nodes: framework.reporting_leads?.length ? (
        <ul className="report-list">
          {framework.reporting_leads.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      ) : null,
    },
    {
      num: '2.6',
      title: 'Records to Request',
      nodes: framework.records_to_request?.length ? (
        <ul className="report-list">
          {framework.records_to_request.map((r, i) => (
            <li key={i}>
              <span className="font-semibold">{r.question}</span>
              {r.record ? ` — ${r.record}` : ''}
            </li>
          ))}
        </ul>
      ) : null,
    },
    {
      num: '2.7',
      title: 'Potential Story Angles',
      nodes: framework.story_angles?.length ? (
        <ul className="report-list">
          {framework.story_angles.map((a, i) => (
            <li key={i}>
              <span className="font-semibold">{a.angle}</span>
              {a.evidence ? ` — evidence: ${a.evidence}` : ''}
              {a.missing ? ` \u00b7 missing: ${a.missing}` : ''}
            </li>
          ))}
        </ul>
      ) : null,
    },
    {
      num: '2.8',
      title: 'Source Map',
      nodes: framework.sources_map?.length ? (
        <ul className="report-list">
          {framework.sources_map.map((sm, i) => (
            <li key={i}>
              <span className="font-semibold">{sm.place}</span>
              {sm.document ? ` — ${sm.document}` : ''}
              {sm.page ? ` (p.${sm.page})` : ''}
            </li>
          ))}
        </ul>
      ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      {subs
        .filter((x) => x.nodes !== null)
        .map((x) => (
          <div key={x.num} className="report-subsection">
            <h4 className="report-h4 report-h4-num">
              {x.num} · {x.title}
            </h4>
            {x.nodes}
          </div>
        ))}
    </div>
  );
}

// ── Result view ───// ── Result view ──────────────────────────────────────────────────────────────

function ResultView({
  digest,
  citations,
  legacyText,
  placeMetaByName,
  digestText,
  running,
  runError,
  currentRunId,
  onDeleteCurrent,
  elapsedSec,
  queuedCount,
}: {
  digest: Digest | null;
  citations: Citation[];
  legacyText?: string;
  placeMetaByName?: Map<string, PlaceOption>;
  digestText?: string;
  running?: boolean;
  runError?: string | null;
  currentRunId?: string | null;
  onDeleteCurrent?: () => void;
  elapsedSec?: number;
  queuedCount?: number;
}) {
  const [showDigest, setShowDigest] = useState(true);

  const generatedTitle = (() => {
    const p = digest?.places?.[0];
    const m = p?.meetings?.[0];
    const metaP = p?.place ? placeMetaByName?.get(p.place) : undefined;
    const state =
      metaP?.path?.[0] && metaP.path[0] !== p?.place ? metaP.path[0] : '';
    const base = [
      p?.place ?? '',
      state ? `, ${state}` : '',
      m?.governing_body ? ` ${m.governing_body}` : '',
      m?.meeting_date ? ` (${fmtDate(m.meeting_date)})` : '',
    ].join('');
    const extra =
      (p?.meetings?.length ?? 0) > 1
        ? ` +${(p?.meetings?.length ?? 1) - 1} more`
        : '';
    return base.trim() ? base + extra : '';
  })();
  const coverage = digest?.coverage;
  const capped =
    coverage?.capped === true || (coverage?.excluded_meetings_count ?? 0) > 0;

  return (
    <div className="space-y-5 print-area">
      {/* Toolbar: Print + Save PDF (report header lives in the masthead) */}
      <div className="print-hide flex items-center justify-end">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={() => setShowDigest((v) => !v)}
          >
            <FileText className="h-4 w-4 mr-1" />
            {showDigest ? 'Hide digest' : 'View digest'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-indigo-600 text-indigo-700 hover:bg-indigo-50"
            onClick={() => window.print()}
          >
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-indigo-600 text-indigo-700 hover:bg-indigo-50"
            onClick={() => window.print()}
          >
            <FileDown className="h-4 w-4 mr-1" /> Save PDF
          </Button>
          {currentRunId && !running && onDeleteCurrent && (
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={onDeleteCurrent}
              title="Delete this summary from your searches"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Generating state — summary is being composed for the chat window */}
      {running && !digest && (
        <Card className="rounded-2xl print-hide">
          <CardContent className="py-8 flex flex-col items-center gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            <p className="text-sm font-medium text-gray-700">
              Generating summary…{' '}
              <span className="text-xs text-gray-400 font-normal">
                {queuedCount} meeting{queuedCount !== 1 ? 's' : ''} queued ·{' '}
                {elapsedSec}s elapsed
              </span>
            </p>
            <p className="text-xs text-gray-400">
              Each meeting is analyzed — larger selections take a few minutes.
              The digest and the follow-up chat will appear here when ready.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Legacy/empty state when there is truly nothing yet (not generating) */}
      {!digest && !running && (
        <Card className="rounded-2xl print-hide">
          <CardContent className="py-6">
            {legacyText ? (
              <div className={MD_CLASSES}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {legacyText}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-gray-500">
                No digest to show.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Run error — shown in this view since Generate lands here */}
      {runError && !running && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 print-hide">
          {runError}
        </div>
      )}

      {/* Collapsible digest — clean SOP-style report */}
      {showDigest && digest && (
        <div className="report-sheet">
          {/* Masthead */}
          <header className="report-masthead">
            <h1 className="report-title report-title-tight">
              {generatedTitle || 'Meeting Digest'}
            </h1>
            <p className="report-sub">
              {digest?.window?.start && digest?.window?.end
                ? `${fmtDate(digest.window.start)} → ${fmtDate(digest.window.end)}`
                : ''}
              {' · '}
              {digest.places?.length ?? 0} place
              {(digest.places?.length ?? 0) !== 1 ? 's' : ''} ·{' '}
              {coverage?.included_meetings ?? 0} meeting
              {(coverage?.included_meetings ?? 1) !== 1 ? 's' : ''} analyzed
            </p>
            <p className="report-meta">
              Report date: {fmtDate(new Date().toISOString())} · Coverage:{' '}
              {capped
                ? `${coverage?.included_meetings ?? 0} meetings included, ${coverage?.excluded_meetings_count ?? 0} left out of this run (cap reached)`
                : 'all matched meetings included'}
              {' · '}
              {citations.length} source document
              {citations.length !== 1 ? 's' : ''}
            </p>
            <hr className="report-rule" />
          </header>

          {/* 1. Overview */}
          <section className="report-section">
            <h2 className="report-h2">1. Overview</h2>
            <p className="report-p">
              This digest summarizes
              {coverage?.included_meetings
                ? ` ${coverage.included_meetings} meeting${coverage.included_meetings !== 1 ? 's' : ''}`
                : ' the selected meetings'}
              {digest?.window?.start && digest?.window?.end
                ? ` between ${fmtDate(digest.window.start)} and ${fmtDate(digest.window.end)}`
                : ''}
              , with every claim linked back to its source document.
            </p>
            {capped && (
              <p className="report-p">
                Note: the requested filters matched more meetings than this
                digest includes ({coverage?.excluded_meetings_count ?? 0} older
                meeting
                {(coverage?.excluded_meetings_count ?? 0) !== 1 ? 's' : ''} were
                left out). The excluded meetings are not summarized here.
              </p>
            )}
            {digest.places_with_no_meetings_in_window?.length ? (
              <p className="report-p">
                Places with no meetings in this window:{' '}
                {digest.places_with_no_meetings_in_window.join(', ')}
              </p>
            ) : null}
          </section>

          {/* 2. Journalist Brief */}
          {digest.framework && (
            <section className="report-section">
              <h2 className="report-h2">2. Journalist Brief</h2>
              <JournalistBrief framework={digest.framework} />
            </section>
          )}

          {/* 3. Meeting Details by Place */}
          <section className="report-section">
            <h2 className="report-h2">3. Meeting Details by Place</h2>
            {(digest.places ?? []).map((place, pi) => (
              <div key={pi} className="report-place">
                <h3 className="report-h3">
                  {place.place}
                  {(() => {
                    const meta = placeMetaByName?.get(place.place);
                    if (!meta) return null;
                    const ancestors = meta.path.slice(0, -1);
                    return (
                      <span className="report-h3-meta">
                        {' '}
                        {meta.level ? `(${typeLabel(meta.level)})` : ''}
                        {ancestors.length > 0
                          ? ` — ${ancestors.join(' → ')}`
                          : ''}
                      </span>
                    );
                  })()}
                </h3>
                {place.headline && <p className="report-p">{place.headline}</p>}
                <div className="space-y-5 report-meeting-list">
                  {(place.meetings ?? []).map((m, mi) => (
                    <div key={mi} className="report-meeting">
                      <h4 className="report-h4">
                        {fmtDate(m.meeting_date)}
                        {m.governing_body ? ` — ${m.governing_body}` : ''}
                        {(m.meeting_types ?? []).length > 0
                          ? ` (${(m.meeting_types ?? []).join(', ')})`
                          : ''}
                        {m.confidence && m.confidence !== 'high'
                          ? ` \u00b7 confidence: ${m.confidence}`
                          : ''}
                      </h4>
                      {m.confidence_reason && (
                        <p className="report-meta report-meta-block">
                          {m.confidence_reason}
                        </p>
                      )}

                      {m.summary && (
                        <div className={MD_CLASSES}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {m.summary}
                          </ReactMarkdown>
                        </div>
                      )}

                      {m.agenda_items &&
                        m.agenda_items.some(
                          (it) =>
                            it.title?.trim() ||
                            it.discussion?.trim() ||
                            it.outcome?.trim() ||
                            (it.people?.length ?? 0) > 0
                        ) && (
                          <div className="mt-3">
                            <p className="report-label">Agenda items</p>
                            <ol className="report-ol">
                              {m.agenda_items
                                .filter(
                                  (it) =>
                                    it.title?.trim() ||
                                    it.discussion?.trim() ||
                                    it.outcome?.trim() ||
                                    (it.people?.length ?? 0) > 0
                                )
                                .map((item, ai) => (
                                  <li key={ai}>
                                    <span className="font-semibold">
                                      {item.title}
                                    </span>
                                    {item.discussion
                                      ? ` — ${item.discussion}`
                                      : ''}
                                    {item.outcome
                                      ? ` \u00b7 Outcome: ${item.outcome}`
                                      : ''}
                                    {item.people && item.people.length > 0
                                      ? ` \u00b7 ${item.people.join(', ')}`
                                      : ''}
                                  </li>
                                ))}
                            </ol>
                          </div>
                        )}

                      {m.motions && m.motions.length > 0 && (
                        <div className="mt-3">
                          <p className="report-label">Motions & votes</p>
                          <ul className="report-list">
                            {m.motions.map((mv, idx) => (
                              <li key={idx}>
                                <span className="font-semibold">
                                  {mv.motion_text}
                                </span>
                                {' \u2014 '}
                                {mv.result ?? 'unknown'}
                                {mv.vote_type ? ` (${mv.vote_type})` : ''}
                                {(mv.moved_by || mv.seconded_by) && (
                                  <>
                                    {' \u2014 '}
                                    {mv.moved_by
                                      ? `moved by ${mv.moved_by}`
                                      : ''}
                                    {mv.moved_by && mv.seconded_by ? '; ' : ''}
                                    {mv.seconded_by
                                      ? `seconded by ${mv.seconded_by}`
                                      : ''}
                                  </>
                                )}
                                {mv.vote_breakdown &&
                                  mv.vote_breakdown.length > 0 && (
                                    <>
                                      {' \u2014 '}
                                      {mv.vote_breakdown
                                        .map((v) => `${v.member}: ${v.vote}`)
                                        .join(', ')}
                                    </>
                                  )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          {/* 4. Sources */}
          {citations.length > 0 && (
            <section className="report-section">
              <h2 className="report-h2">4. Sources</h2>
              <ul className="report-list">
                {citations.map((c, i) => (
                  <li key={i}>
                    {c.artifactUrl ? (
                      <a
                        href={c.artifactUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="report-link"
                      >
                        {c.artifactTitle ?? c.artifactUrl}
                      </a>
                    ) : (
                      <span>{c.artifactTitle ?? c.artifactId}</span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
      {!showDigest && digestText && !running && (
        <p className="print-hide text-xs text-gray-500">
          Digest hidden — the chat below still references it.
        </p>
      )}

      {/* Follow-up chat — the main surface (normal-chat layout) */}
      {digestText && !running && (
        <DigestChat digestText={digestText} citations={citations} />
      )}

      {/* Sources */}
      {citations.length > 0 && (
        <Card className="rounded-2xl print-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" /> Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1.5 flex-wrap">
              {citations.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1"
                >
                  {c.artifactUrl ? (
                    <a
                      href={c.artifactUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate max-w-[220px]"
                    >
                      {c.artifactTitle ?? c.artifactUrl}
                    </a>
                  ) : (
                    <span className="truncate max-w-[220px]">
                      {c.artifactTitle ?? c.artifactId}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
